import React, { useState } from 'react';
import { Alert, Button, Card, Checkbox, Divider, List, Modal, Space, Table, Tag, Typography } from 'antd';

const { Text } = Typography;

const formatPercent = (value) => value != null ? `${Number(value).toFixed(1)}%` : '-';
const numericValue = (value) => Number(value || 0);

const dropColumns = [
  { title: 'Player', dataIndex: 'playerName', key: 'playerName', fixed: 'left', width: 170, sorter: (a, b) => (a.playerName || '').localeCompare(b.playerName || '') },
  { title: 'Team', dataIndex: 'team', key: 'team', width: 70, sorter: (a, b) => (a.team || '').localeCompare(b.team || '') },
  { title: 'Pos', dataIndex: 'position', key: 'position', width: 55, sorter: (a, b) => (a.position || '').localeCompare(b.position || '') },
  { title: 'Depth', dataIndex: 'depthChart', key: 'depthChart', width: 70, sorter: (a, b) => (a.depthChart || '').localeCompare(b.depthChart || '') },
  { title: 'Own %', dataIndex: 'ownPercentage', key: 'ownPercentage', width: 75, render: formatPercent, sorter: (a, b) => numericValue(a.ownPercentage) - numericValue(b.ownPercentage) },
  { title: 'Drop %', dataIndex: 'dropPercentage', key: 'dropPercentage', width: 75, render: formatPercent, sorter: (a, b) => numericValue(a.dropPercentage) - numericValue(b.dropPercentage) },
  { title: 'Route %', dataIndex: 'routeParticipation', key: 'routeParticipation', width: 80, render: formatPercent, sorter: (a, b) => numericValue(a.routeParticipation) - numericValue(b.routeParticipation) },
  { title: 'TPRR', dataIndex: 'targetsPerRouteRun', key: 'targetsPerRouteRun', width: 70, render: formatPercent, sorter: (a, b) => numericValue(a.targetsPerRouteRun) - numericValue(b.targetsPerRouteRun) },
  { title: 'Carry Share', dataIndex: 'carryShare', key: 'carryShare', width: 90, render: formatPercent, sorter: (a, b) => numericValue(a.carryShare) - numericValue(b.carryShare) }
];

const MoveList = ({ title, players, tagColor }) => (
  <div>
    <Text strong>{title}</Text>
    <List
      size="small"
      dataSource={players}
      locale={{ emptyText: 'None' }}
      renderItem={(player) => (
        <List.Item>
          <Space>
            <Tag color={tagColor}>{player.position || 'PLAYER'}</Tag>
            <Text>{player.playerName}</Text>
            <Text type="secondary">{player.team || '-'}</Text>
          </Space>
        </List.Item>
      )}
    />
  </div>
);

const ActivationList = ({ players, droppedPlayerIDs, onDropInstead }) => (
  <div>
    <Text strong>Activate from IR</Text>
    <List
      size="small"
      dataSource={players}
      locale={{ emptyText: 'None' }}
      renderItem={(player) => (
        <List.Item
          actions={[
            <Checkbox
              key="drop-instead"
              checked={droppedPlayerIDs.includes(String(player.playerID))}
              onChange={(event) => onDropInstead(player, event.target.checked)}
            >
              Drop instead
            </Checkbox>
          ]}
        >
          <Space>
            <Tag color="green">{player.position || 'PLAYER'}</Tag>
            <Text>{player.playerName}</Text>
            <Text type="secondary">{player.team || '-'}</Text>
          </Space>
        </List.Item>
      )}
    />
  </div>
);

const IRPanel = ({ leagues, onPreview, onSubmit }) => {
  const [previews, setPreviews] = useState(null);
  const [selectedDrops, setSelectedDrops] = useState({});
  const [selectedIRDrops, setSelectedIRDrops] = useState({});
  const [dropModalLeague, setDropModalLeague] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setSubmitResult(null);
    setSelectedDrops({});
    setSelectedIRDrops({});
    try {
      setPreviews(await onPreview());
    } catch (err) {
      setError(err.message || 'Failed to preview IR moves');
    } finally {
      setLoading(false);
    }
  };

  const setLeagueDrops = (leagueID, drops) => {
    setSelectedDrops((previous) => ({ ...previous, [leagueID]: drops }));
  };

  const setIRDrop = (leagueID, player, shouldDrop) => {
    setSelectedIRDrops((previous) => {
      const current = previous[leagueID] || [];
      const playerID = String(player.playerID);
      const next = shouldDrop
        ? [...new Set([...current, playerID])]
        : current.filter((id) => id !== playerID);
      return { ...previous, [leagueID]: next };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const moves = previews.map((preview) => ({
        leagueID: preview.leagueID,
        activate: preview.toActivate
          .filter((player) => !(selectedIRDrops[preview.leagueID] || []).includes(String(player.playerID)))
          .map((player) => player.playerID),
        deactivate: preview.toDeactivate.map((player) => player.playerID),
        drop: [
          ...(selectedDrops[preview.leagueID] || []).map((player) => player.playerID),
          ...(selectedIRDrops[preview.leagueID] || [])
        ]
      }));
      const result = await onSubmit(moves);
      setSubmitResult(result);
    } catch (err) {
      setError(err.message || 'Failed to submit IR moves');
    } finally {
      setSubmitting(false);
    }
  };

  const allDropsSelected = previews && previews.every((preview) => {
    const irDrops = selectedIRDrops[preview.leagueID] || [];
    const remainingSpotsNeeded = Math.max(0, preview.spotsNeeded - irDrops.length);
    return (selectedDrops[preview.leagueID] || []).length >= remainingSpotsNeeded;
  });
  const hasMoves = previews && previews.length > 0;

  return (
    <div>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handlePreview} loading={loading} disabled={leagues.length === 0}>
          Load IR Moves for All Leagues
        </Button>
        {leagues.length > 0 && <Text type="secondary">{leagues.length} leagues connected</Text>}
      </Space>

      {error && <Alert style={{ marginBottom: 16 }} type="error" message={error} />}
      {previews && previews.length === 0 && <Alert type="success" message="No IR moves are currently needed." />}

      {previews && previews.map((preview) => {
        const chosenDrops = selectedDrops[preview.leagueID] || [];
        const irDrops = selectedIRDrops[preview.leagueID] || [];
        const remainingSpotsNeeded = Math.max(0, preview.spotsNeeded - irDrops.length);
        const enoughDrops = chosenDrops.length >= remainingSpotsNeeded;
        return (
          <Card key={preview.leagueID} title={`League: ${preview.leagueName} (${preview.leagueID})`} style={{ marginBottom: 16 }}>
            <MoveList title="Move to IR" players={preview.toDeactivate} tagColor="orange" />
            <Divider style={{ margin: '12px 0' }} />
            <ActivationList
              players={preview.toActivate}
              droppedPlayerIDs={irDrops}
              onDropInstead={(player, shouldDrop) => setIRDrop(preview.leagueID, player, shouldDrop)}
            />

            {preview.spotsNeeded > 0 && (
              <div style={{ marginTop: 16 }}>
                <Alert
                  type="warning"
                  message={`${remainingSpotsNeeded} additional drop${remainingSpotsNeeded === 1 ? '' : 's'} needed to stay under the ${preview.rosterSize}-player roster limit.`}
                />
                <Button style={{ marginTop: 10 }} onClick={() => setDropModalLeague(preview)}>
                  {enoughDrops ? `${chosenDrops.length} Drop${chosenDrops.length === 1 ? '' : 's'} Selected` : 'Choose Drops'}
                </Button>
              </div>
            )}
          </Card>
        );
      })}

      {hasMoves && (
        <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={!allDropsSelected}>
          Submit All IR Moves
        </Button>
      )}

      {submitResult && <Alert style={{ marginTop: 16 }} type="success" message="IR moves submitted for all affected leagues." />}

      <Modal
        title={dropModalLeague ? `Choose drops for ${dropModalLeague.leagueName}` : 'Choose drops'}
        visible={Boolean(dropModalLeague)}
        width={1050}
        onCancel={() => setDropModalLeague(null)}
        onOk={() => setDropModalLeague(null)}
        okText="Done"
      >
        {dropModalLeague && (
          <Table
            rowKey={(player) => player.playerID}
            columns={dropColumns}
            dataSource={dropModalLeague.dropCandidates}
            rowSelection={{
              selectedRowKeys: (selectedDrops[dropModalLeague.leagueID] || []).map((player) => player.playerID),
              onChange: (_keys, rows) => setLeagueDrops(dropModalLeague.leagueID, rows)
            }}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 760 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default IRPanel;